import React, { useEffect, useState } from "react";
import ClientLayout from "../../components/client/ClientLayout";
import RequireClientAdmin from "../../components/client/RequireClientAdmin";
import SyncStatusBanner from "../../components/client/SyncStatusBanner";
import { clientApi } from "../../api";
import { formatDateAU } from "../../utils/formatDate";

export default function DuplicatesReport() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null); // { firstname, surname, dob, members }
  const [mergeBusy, setMergeBusy] = useState(false);
  const [survivor, setSurvivor] = useState(null);
  const [losers, setLosers] = useState([]);
  const [reason, setReason] = useState("");

  async function load() {
    setLoading(true); setError("");
    try { const d = await clientApi.getDuplicateGroups(); setGroups(d.groups || []); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function open(g) {
    const firstname = g.firstname || g.FIRSTNAME;
    const surname = g.surname || g.SURNAME;
    const dob = g.dateofbirth || g.DATEOFBIRTH;
    const dobIso = dob ? String(dob).split("T")[0] : "";
    try {
      const d = await clientApi.getDuplicateGroupMembers(firstname, surname, dobIso);
      setActive({ firstname, surname, dob: dobIso, members: d.members || [] });
      setSurvivor(null); setLosers([]); setReason("");
    } catch (err) { alert(err.message); }
  }

  function toggleLoser(id) {
    if (id === survivor) return;
    setLosers((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);
  }

  async function doMerge() {
    if (!survivor) return alert("Pick a survivor first.");
    if (!losers.length) return alert("Select at least one duplicate to merge.");
    if (!window.confirm(`Merge ${losers.length} duplicate(s) into #${survivor}? The duplicates will be suspended in S7000.`)) return;
    setMergeBusy(true);
    try {
      const r = await clientApi.mergeMembers({
        survivorMembershipId: survivor,
        loserMembershipIds: losers,
        reason,
      });
      alert(`Merged. Survivor: #${r.survivor}. ${r.results.filter((x) => x.ok).length}/${r.results.length} losers suspended.`);
      setActive(null);
      await load();
    } catch (err) { alert(err.message); }
    setMergeBusy(false);
  }

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Duplicate Members</h1>
          <p className="page-subtitle">Candidates matched by first name + last name + date of birth</p>
        </div>
      </div>

      <SyncStatusBanner />

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? <div style={{ color: "var(--text-muted)" }}>Loading duplicates from local cache…</div> : (
        !groups.length ? <div className="empty-state"><h3>No duplicate candidates</h3><p>Nothing matches firstname + surname + DOB.</p></div> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>First name</th><th>Surname</th><th>DOB</th><th>Dupes</th><th></th></tr></thead>
              <tbody>
                {groups.map((g, i) => (
                  <tr key={i}>
                    <td>{g.firstname || g.FIRSTNAME}</td>
                    <td>{g.surname || g.SURNAME}</td>
                    <td>{formatDateAU(g.dateofbirth || g.DATEOFBIRTH, "")}</td>
                    <td style={{ fontWeight: 600 }}>{g.dupes || g.DUPES}</td>
                    <td><button className="btn btn-outline btn-sm" onClick={() => open(g)}>Review</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {active && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setActive(null)}>
          <div className="modal" style={{ maxWidth: 820 }}>
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>Resolve duplicates for {active.firstname} {active.surname}</h2>
                <p>Pick one survivor (👑) and select the duplicates to suspend.</p>
              </div>
              <button className="modal-close-btn" onClick={() => setActive(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>Keep as survivor</th><th>Merge</th><th>Member #</th><th>Email</th><th>Mobile</th><th>Registered</th><th>Status</th></tr></thead>
                  <tbody>
                    {active.members.map((m) => {
                      const id = String(m.membernumber || m.MEMBERNUMBER);
                      return (
                        <tr key={id}>
                          <td><input type="radio" name="survivor" checked={survivor === id} onChange={() => { setSurvivor(id); setLosers((xs) => xs.filter((x) => x !== id)); }} /></td>
                          <td><input type="checkbox" checked={losers.includes(id)} onChange={() => toggleLoser(id)} disabled={survivor === id} /></td>
                          <td><code>#{id}</code></td>
                          <td style={{ fontSize: "0.85rem" }}>{m.email || m.EMAIL || "—"}</td>
                          <td style={{ fontSize: "0.85rem" }}>{m.mobile || m.MOBILE || "—"}</td>
                          <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{formatDateAU(m.dateregistered || m.DATEREGISTERED, "")}</td>
                          <td>{(m.membershipstatus || m.MEMBERSHIPSTATUS) === "1" ? <span className="badge active">Active</span> : <span className="badge inactive">Inactive</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="form-field" style={{ marginTop: "1rem" }}>
                <label>Reason (logged)</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. duplicate created during migration" />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setActive(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={mergeBusy || !survivor || !losers.length} onClick={doMerge}>
                {mergeBusy ? "Merging…" : `Merge ${losers.length} into #${survivor || "?"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
